/**
 * Pure transforms behind the drawer's package picker: expanded package →
 * next draft (fresh ids, CUSTOM forced, attribution stamped) and the
 * "does the draft hold work worth confirming over?" predicate.
 * For Media Devoted | Created By Ciro Autiero
 */

import type {
  FlowOptimizerConfig,
  FlowOptimizerPackageExpanded,
} from '@trafficpulse/shared';

import { replaceFormulaFromPayload } from '@/features/flow-optimizer/clipboard/formula-clipboard.apply';

import { applyPackageToDraft } from '../envelope/apply-package';
import { applyRuleSetPayload } from '../envelope/apply-rule-set';
import { buildFormulaSetPayload } from '../envelope/serialize-library';
import type { AssembledLibraryPayload } from '../validate/assemble-accepted';

export function applyExpandedPackageToDraft(
  draft: FlowOptimizerConfig,
  pkg: FlowOptimizerPackageExpanded,
  knownBankIds?: ReadonlySet<string>,
): FlowOptimizerConfig {
  const next = applyPackageToDraft({
    draft,
    payload: {
      // Strip ids via the payload serializer so paste regenerates them.
      formula: buildFormulaSetPayload(pkg.formula),
      rules: {
        excludeRules: pkg.excludeRules,
        capacityRules: pkg.capacityRules,
      },
    },
    knownBankIds,
  });
  // Attribution travels with the draft — Save Config persists it.
  return { ...next, packageId: pkg.id };
}

/** Accepted AI items → next draft: each half replaces only its own tab. */
export function applyAssembledToDraft(
  draft: FlowOptimizerConfig,
  assembled: AssembledLibraryPayload,
): FlowOptimizerConfig {
  let next = { ...draft, mode: 'CUSTOM' as const };
  if (assembled.formula) {
    next = { ...next, formula: replaceFormulaFromPayload(assembled.formula) };
  }
  if (assembled.rules) {
    const applied = applyRuleSetPayload(assembled.rules);
    next = {
      ...next,
      excludeRules: applied.excludeRules,
      capacityRules: applied.capacityRules,
    };
  }
  return next;
}

/** True when loading a package would overwrite something the user built. */
export function draftHasWork(draft: FlowOptimizerConfig): boolean {
  const { excludeRules } = draft;
  return (
    draft.formula.groups.length > 0 ||
    Boolean(excludeRules.paths || excludeRules.landers || excludeRules.offers) ||
    (draft.capacityRules?.length ?? 0) > 0
  );
}
